import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CheckCircle2, CircleAlert, Info, X } from "lucide-react";

const ToastContext = createContext({
  dismissToast: () => {},
  showToast: () => "",
});

const toneIcons = {
  danger: CircleAlert,
  info: Info,
  success: CheckCircle2,
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const nextIdRef = useRef(0);
  const timersRef = useRef(new Map());

  const dismissToast = useCallback((id) => {
    window.clearTimeout(timersRef.current.get(id));
    timersRef.current.delete(id);
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback((input) => {
    const toast = typeof input === "string" ? { message: input } : input;
    const id = `toast-${nextIdRef.current += 1}`;
    const duration = Math.max(1200, Number(toast.duration) || 2400);
    setToasts((current) => [
      ...current.slice(-3),
      {
        id,
        message: toast.message,
        tone: toast.tone || "info",
      },
    ]);
    timersRef.current.set(
      id,
      window.setTimeout(() => dismissToast(id), duration),
    );
    return id;
  }, [dismissToast]);

  useEffect(() => () => {
    timersRef.current.forEach((timer) => window.clearTimeout(timer));
    timersRef.current.clear();
  }, []);

  const value = useMemo(
    () => ({ dismissToast, showToast }),
    [dismissToast, showToast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="suite-toast-viewport"
        aria-live="polite"
        aria-atomic="false"
      >
        {toasts.map((toast) => {
          const Icon = toneIcons[toast.tone] || Info;
          return (
            <div
              className={`suite-toast is-${toast.tone}`}
              key={toast.id}
              role={toast.tone === "danger" ? "alert" : "status"}
            >
              <Icon size={17} aria-hidden="true" />
              <span>{toast.message}</span>
              <button
                type="button"
                aria-label="Cerrar mensaje"
                onClick={() => dismissToast(toast.id)}
              >
                <X size={14} aria-hidden="true" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
